import { useRef, useState, useEffect } from 'react'
import { motion, useInView } from 'framer-motion'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import HeroSection from '../components/HeroSection'
import SEO from '../components/SEO'
import InteractiveLibroGame from '../components/InteractiveLibroGame'
import D20WillpowerRoll from '../components/D20WillpowerRoll'
import AvatarDebuffAnalyzer from '../components/AvatarDebuffAnalyzer'
import FloatingHUDQuestTracker from '../components/FloatingHUDQuestTracker'
import WhatHappensSection from '../components/WhatHappensSection'
import DifferenceSection from '../components/DifferenceSection'
import BossSection from '../components/BossSection'
import MethodSection from '../components/MethodSection'
import AnalysisSection from '../components/AnalysisSection'

const homeSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "Nerd Coach",
  "image": "https://nerdcoach.it/og-image.png",
  "@id": "https://nerdcoach.it/#organization",
  "url": "https://nerdcoach.it",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "IT"
  },
  "sameAs": [
    "https://instagram.com/nerdcoach.it",
    "https://tiktok.com/@nerdcoach.it",
    "https://yourbraincoach.it"
  ],
  "description": "Coaching mentale e fisico per nerd, anime lover, gamer e persone bloccate: focus, stress, energia, corpo e azione concreta."
}

const archetypes = [
  {
    name: 'Il Player Bloccato',
    id: 'player-bloccato',
    desc: 'Sa cosa dovrebbe fare, ma continua a rimandare nei loop dopaminergici.',
    stats: { Focus: 40, Energia: 35, Azione: 20, Coraggio: 30 },
    color: '#8b5cf6',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4l3 3" />
      </svg>
    ),
  },
  {
    name: 'Il Mago Bruciato',
    id: 'mago-bruciato',
    desc: 'Ha la testa piena di idee e progetti, ma energia azzerata e troppi loop aperti.',
    stats: { Focus: 25, Energia: 20, Azione: 30, Coraggio: 45 },
    color: '#00d4ff',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M12 2L2 12l10 10 10-10L12 2z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
  {
    name: 'Il Tank Silenzioso',
    id: 'tank-silenzioso',
    desc: 'Regge enormi carichi di stress senza battere ciglio, ma non chiede mai supporto.',
    stats: { Focus: 55, Energia: 40, Azione: 50, Coraggio: 35 },
    color: '#ef4444',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    name: 'Il Rogue Invisibile',
    id: 'rogue-invisibile',
    desc: 'Osserva, capisce ed elabora piani infallibili, ma si nasconde per paura di fallire.',
    stats: { Focus: 60, Energia: 30, Azione: 25, Coraggio: 20 },
    color: '#9ca3af',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
  {
    name: 'Il Chierico degli Altri',
    id: 'chierico-degli-altri',
    desc: 'Risolve i problemi di tutto il party, lasciando le proprie quest per ultime.',
    stats: { Focus: 45, Energia: 25, Azione: 55, Coraggio: 40 },
    color: '#facc15',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      </svg>
    ),
  },
]

const roadmapQuests = [
  {
    level: "Lv. 01 — La Safe Zone",
    title: "Dungeon di Partenza",
    desc: "Identificazione ed eliminazione attiva dell'evitamento dopaminergico primario e dello scrolling mattutino.",
    loot: "Sblocco Focus Primario · Riattivazione Energia Fisica",
    stats: { Azione: "+15", Focus: "+10" },
    color: "#8b5cf6"
  },
  {
    level: "Lv. 10 — Il Risveglio",
    title: "Sblocco della Classe",
    desc: "Mappatura dei valori e allineamento reale. Scomposizione dei task in sub-quest concrete ad ambiguità zero.",
    loot: "Diario di Bordo Sbloccato · Identificazione Archetipo",
    stats: { Focus: "+25", Autonomia: "+15" },
    color: "#00d4ff"
  },
  {
    level: "Lv. 25 — La Spedizione",
    title: "Campagna Reale",
    desc: "Consolidamento dell'autodisciplina corporea con allenamenti nerd-oriented, bio-ottimizzazione e costanza.",
    loot: "Abilità Passiva 'Focus Continuo' · Costanza Sbloccata",
    stats: { Grinta: "+30", Energia: "+25" },
    color: "#ef4444"
  },
  {
    level: "Lv. 50+ — La Maestria",
    title: "Boss Finale Sconfitto",
    desc: "Pieno controllo autogestito del proprio tempo, del proprio corpo e della mente reale. Pronto per il multi-questing lavorativo offline.",
    loot: "Status Titolo: Pro Player Offline (100% Autonomo)",
    stats: { Autodisciplina: "MAX", Focus: "MAX" },
    color: "#facc15"
  }
]

function ArchetypeHomeCard({ archetype, index }) {
  const cardRef = useRef(null)
  const navigate = useNavigate()

  const handleMouseMove = (e) => {
    const card = cardRef.current
    if (!card) return
    const rect = card.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const cx = rect.width / 2
    const cy = rect.height / 2
    card.style.transform = `perspective(1000px) rotateX(${(y - cy) / 18}deg) rotateY(${(cx - x) / 18}deg) scale3d(1.01,1.01,1.01)`
  }
  const handleMouseLeave = () => {
    const card = cardRef.current
    if (card) card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1,1,1)'
  }

  const handleClick = () => {
    navigate(`/archetipi#${archetype.id}`)
  }

  return (
    <div
      ref={cardRef}
      onClick={handleClick}
      className="manga-panel rounded-xl p-5 cursor-pointer relative overflow-hidden group flex flex-col justify-between"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ transition: 'transform 0.15s ease-out, border-color 0.3s ease', minHeight: '340px' }}
    >
      <div
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ boxShadow: `inset 0 0 25px ${archetype.color}12, 0 0 30px ${archetype.color}08` }}
      />
      
      <div className="relative z-10 space-y-4">
        {/* Card Header */}
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ color: archetype.color, background: `${archetype.color}12` }}
          >
            {archetype.icon}
          </div>
          <div>
            <h3 className="font-display text-base font-bold text-nerd-white group-hover:text-gradient transition-all">{archetype.name}</h3>
            <span className="text-[8px] text-nerd-gray uppercase tracking-widest font-display">Archetipo {String(index + 1).padStart(2, '0')}</span>
          </div>
        </div>

        {/* Desc */}
        <p className="text-nerd-gray text-xs leading-relaxed font-body">
          {archetype.desc}
        </p>

        {/* Stats */}
        <div className="space-y-1.5 pt-2">
          {Object.entries(archetype.stats).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2">
              <span className="text-[8px] text-nerd-gray uppercase w-14 shrink-0 font-display">{k}</span>
              <div className="flex-1 h-1 bg-nerd-dark rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${v}%`, background: `linear-gradient(90deg, ${archetype.color}50, ${archetype.color})` }}
                />
              </div>
              <span className="text-[9px] font-display w-6 text-right" style={{ color: archetype.color }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="relative z-10 pt-4">
        <div
          className="w-full text-center py-2 block rounded-lg text-[10px] font-display font-semibold uppercase tracking-wider transition-all duration-300"
          style={{
            background: 'transparent',
            color: archetype.color,
            border: `1px solid ${archetype.color}35`,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = `${archetype.color}08`
            e.currentTarget.style.borderColor = archetype.color
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent'
            e.currentTarget.style.borderColor = `${archetype.color}35`
          }}
        >
          Vedi Analisi Classe →
        </div>
      </div>
    </div>
  )
}

export default function HomePage() {
  const { hash } = useLocation()
  const heroRef = useRef(null)
  const archetypeRef = useRef(null)
  const questlineRef = useRef(null)
  const testimonialRef = useRef(null)
  
  const heroInView = useInView(heroRef, { once: true, margin: '-50px' })
  const archetypeInView = useInView(archetypeRef, { once: true, margin: '-50px' })
  const questlineInView = useInView(questlineRef, { once: true, margin: '-50px' })
  const testimonialInView = useInView(testimonialRef, { once: true, margin: '-50px' })

  useEffect(() => {
    if (hash) {
      const elementId = hash.replace('#', '')
      const element = document.getElementById(elementId)
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 200)
      }
    }
  }, [hash])

  return (
    <>
      <SEO 
        title="Sblocca il tuo potenziale nella vita reale" 
        description="Coaching mentale e fisico per nerd, anime lover, gamer e persone bloccate. Esci dai loop di evitamento e sblocca l'azione concreta nella vita reale."
        schema={homeSchema}
      />
      
      {/* Floating HUD Quest Tracker */}
      <FloatingHUDQuestTracker />

      {/* INTRO CINEMATICO */}
      <div id="hero-top">
        <HeroSection />
      </div>

      {/* SEZIONE DADO D20 INIZIATIVA */}
      <section id="iniziativa" className="relative w-full py-12 md:py-18 px-4" style={{ background: '#050510' }}>
        <div className="max-w-6xl mx-auto text-center mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[10px] text-nerd-purple font-display uppercase tracking-[0.2em]">Quest Log: Iniziativa</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-nerd-white mt-1 mb-2">
              Sei pronto a <span className="text-gradient">reagire?</span>
            </h2>
            <p className="text-nerd-gray text-xs sm:text-sm max-w-md mx-auto">
              La mente corre online, ma il corpo è bloccato. Tira il dado e verifica le tue statistiche mattutine.
            </p>
          </motion.div>
        </div>
        <D20WillpowerRoll />
      </section>

      {/* BLOCCO 1 — HERO SPLIT (HOOK) */}
      <section
        ref={heroRef}
        className="relative w-full py-12 md:py-18 px-4"
        style={{ background: '#050510' }}
      >
        <div className="max-w-6xl mx-auto w-full">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-14">
            {/* SINISTRA — Testo */}
            <motion.div
              className="flex-1 md:w-[60%] text-center md:text-left"
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-5">
                <span className="text-gradient">Campione nei mondi virtuali,</span>
                <br />
                <span className="text-gradient">ma bloccato nella vita reale?</span>
              </h2>
              <p className="text-nerd-gray text-lg leading-relaxed mb-8 max-w-lg mx-auto md:mx-0 font-body">
                Oggi aiuto nerd, anime lover e gamer a sbloccare l'autodisciplina e tornare forti nella vita reale. Senza smettere di sognare.
              </p>
              <div className="flex flex-col items-center md:items-start gap-4">
                <div className="flex flex-wrap justify-center md:justify-start gap-3">
                  <a href="#lead-game" className="nerd-btn-primary text-xs inline-block">
                    Gioca il Libro-Game 🎮
                  </a>
                  <Link to="/nerd-power" className="nerd-btn-secondary text-xs inline-block">
                    Calibra Nerd Power ⚡
                  </Link>
                </div>
                <Link
                  to="/metodo"
                  className="text-nerd-cyan text-xs hover:text-nerd-blue transition-colors underline underline-offset-4 font-display font-medium uppercase tracking-wider"
                >
                  Oppure scopri il metodo →
                </Link>
              </div>
            </motion.div>

            {/* DESTRA — Immagine */}
            <motion.div
              className="md:w-[40%] flex justify-center"
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="relative" style={{ width: '260px' }}>
                <div className="absolute -inset-3 rounded-xl pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(139,92,246,0.18) 0%, transparent 70%)' }} />

                <div
                  className="relative rounded-xl overflow-hidden"
                  style={{
                    border: '1px solid rgba(139,92,246,0.45)',
                    boxShadow: '0 8px 32px rgba(139,92,246,0.25), 0 0 60px rgba(139,92,246,0.12)',
                    background: '#050510',
                  }}
                >
                  <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid rgba(139,92,246,0.2)', background: 'rgba(139,92,246,0.06)' }}>
                    <span className="font-display text-[9px] uppercase tracking-widest text-nerd-purple">Nerd Coach</span>
                    <span className="font-display text-[9px] uppercase tracking-widest text-nerd-gray/50">Coach</span>
                  </div>

                  <div className="relative" style={{ height: '300px', background: '#050510' }}>
                    <img
                      src="/amedeo.jpg"
                      alt="Amedeo Sbardellotto — Nerd Coach"
                      className="w-full h-full object-cover object-top"
                      style={{ filter: 'contrast(1.05) brightness(0.95)' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                        e.currentTarget.nextSibling.style.display = 'flex'
                      }}
                    />
                    <div className="w-full h-full bg-nerd-card items-center justify-center text-nerd-gray text-sm absolute inset-0" style={{ display: 'none' }}>
                      [FOTO AMEDEO]
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none" style={{ background: 'linear-gradient(to top, #050510 0%, transparent 100%)' }} />
                    <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(139,92,246,0.015) 3px, rgba(139,92,246,0.015) 4px)' }} />
                  </div>

                  <div className="px-3 py-2" style={{ borderTop: '1px solid rgba(139,92,246,0.2)', background: 'rgba(139,92,246,0.04)' }}>
                    <div className="font-display text-[10px] text-nerd-white tracking-wider">AMEDEO</div>
                    <div className="font-display text-[8px] text-nerd-gray/60 uppercase tracking-widest">Coach · Personal Trainer</div>
                  </div>

                  <div className="absolute top-8 left-1.5 w-3 h-3 border-l border-t border-nerd-cyan/30" />
                  <div className="absolute top-8 right-1.5 w-3 h-3 border-r border-t border-nerd-cyan/30" />
                  <div className="absolute bottom-8 left-1.5 w-3 h-3 border-l border-b border-nerd-cyan/30" />
                  <div className="absolute bottom-8 right-1.5 w-3 h-3 border-r border-b border-nerd-cyan/30" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BLOCCO 2 — DIAGNOSI & DEBUFF SIMULATOR */}
      <div id="diagnosi">
        <WhatHappensSection />
        <AvatarDebuffAnalyzer />
      </div>

      {/* BLOCCO 3 — DIFFERENZA DI METODO */}
      <div id="differenza">
        <DifferenceSection />
      </div>

      {/* BLOCCO 4 — ARCHETIPI & BOSS */}
      <section
        ref={archetypeRef}
        id="archetipo"
        className="relative w-full py-12 md:py-18 px-4"
        style={{ background: '#050510' }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={archetypeInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-nerd-white mb-4">
                Scegli il tuo{' '}
                <span className="text-gradient">archetipo.</span>
              </h2>
              <p className="text-nerd-gray text-sm md:text-base max-w-xl mx-auto">
                In quale classe ti sei incastrato? Mappa i tuoi blocchi reali, analizza le statistiche e sblocca la build per tornare ad agire.
              </p>
            </motion.div>
          </div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mb-14"
            initial={{ opacity: 0, y: 30 }}
            animate={archetypeInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {archetypes.map((a, i) => (
              <ArchetypeHomeCard key={a.name} archetype={a} index={i} />
            ))}
          </motion.div>
        </div>
        
        {/* Boss Section integrata sotto come ostacolo di classe */}
        <BossSection />
      </section>

      {/* BLOCCO 5 — METODO */}
      <MethodSection />

      {/* BLOCCO 6 — ANALISI CANALI (SCANSIONE) */}
      <AnalysisSection />

      {/* BLOCCO 7 — QUESTLINE (ROADMAP DI GILDA) */}
      <section
        ref={questlineRef}
        id="roadmap"
        className="relative w-full py-12 md:py-18 px-4 border-t border-nerd-purple/5"
        style={{ background: '#050510' }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={questlineInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-nerd-white mb-4">
                La <span className="text-gradient">Questline</span> del Percorso
              </h2>
              <p className="text-nerd-gray text-sm md:text-base max-w-xl mx-auto">
                La progressione a livelli del tuo coaching. Nessun trucco. Solo costanza strutturata per sconfiggere i boss della procrastinazione reale.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
            {/* Tech grid path line in background for md and up */}
            <div className="hidden md:block absolute top-[15%] bottom-[15%] left-1/2 w-[1px] bg-gradient-to-b from-nerd-purple/20 via-nerd-cyan/20 to-transparent -translate-x-1/2 pointer-events-none" />

            {roadmapQuests.map((quest, idx) => (
              <motion.div
                key={idx}
                className="manga-panel rounded-xl p-6 relative overflow-hidden"
                style={{
                  border: `1px solid ${quest.color}25`,
                  background: 'rgba(5, 5, 20, 0.45)',
                }}
                initial={{ opacity: 0, y: 30 }}
                animate={questlineInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
              >
                {/* Visual Level badge */}
                <div
                  className="absolute top-0 right-0 px-3 py-1 text-[9px] font-display font-semibold rounded-bl uppercase tracking-wider"
                  style={{ color: quest.color, background: `${quest.color}15`, borderLeft: `1px solid ${quest.color}25`, borderBottom: `1px solid ${quest.color}25` }}
                >
                  {quest.level}
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-display text-lg font-bold text-nerd-white mb-1">{quest.title}</h3>
                    <p className="text-nerd-gray text-xs md:text-sm leading-relaxed font-body">{quest.desc}</p>
                  </div>

                  <div className="pt-2 border-t border-nerd-purple/10">
                    <span className="text-[8px] text-nerd-cyan uppercase font-display block tracking-widest mb-1.5">Loot Sbloccato:</span>
                    <p className="text-nerd-white text-xs font-semibold">{quest.loot}</p>
                  </div>

                  <div className="flex gap-4">
                    {Object.entries(quest.stats).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-1.5">
                        <span className="text-[8px] text-nerd-gray uppercase font-display">{k}:</span>
                        <span className="text-xs font-display font-bold" style={{ color: quest.color }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOCCO 8 — TESTIMONIANZE */}
      <section
        ref={testimonialRef}
        className="relative w-full py-12 md:py-18 px-4 border-t border-nerd-purple/5"
        style={{ background: '#050510' }}
      >
        <div className="max-w-3xl mx-auto">
          <motion.div
            className="manga-panel rounded-xl p-6 md:p-10 text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="text-nerd-white text-lg md:text-xl leading-relaxed italic mb-6">
              “Per la prima volta qualcuno ha capito perché non riuscivo ad alzarmi dal letto. Amedeo non mi ha detto ‘svegliati alle 5’. Mi ha aiutato a capire il mio sistema.”
            </p>
            <p className="text-nerd-gray text-sm font-display uppercase tracking-wider mb-8">
              M., 28 anni — Player Bloccato
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
              <div className="text-center">
                <div className="font-display text-2xl font-bold text-gradient">120+</div>
                <div className="text-nerd-gray text-xs uppercase tracking-wider mt-1">sessioni svolte</div>
              </div>
              <div className="hidden sm:block w-[1px] h-8 bg-nerd-purple/20" />
              <div className="text-center">
                <div className="font-display text-2xl font-bold text-gradient">85%</div>
                <div className="text-nerd-gray text-xs uppercase tracking-wider mt-1">tornano ad allenarsi</div>
              </div>
              <div className="hidden sm:block w-[1px] h-8 bg-nerd-purple/20" />
              <div className="text-center">
                <div className="font-display text-2xl font-bold text-gradient">4</div>
                <div className="text-nerd-gray text-xs uppercase tracking-wider mt-1">anni di esperienza</div>
              </div>
            </div>

            <p className="text-nerd-gray/60 text-xs mt-8 uppercase tracking-wider font-display">
              Prime testimonianze in arrivo.
            </p>
          </motion.div>
        </div>
      </section>

      {/* BLOCCO 9 — LIBRO-GAME LEAD CAPTURE IN HOME */}
      <section
        id="lead-game"
        className="relative w-full py-12 md:py-18 px-4 border-t border-nerd-purple/5"
        style={{ background: '#050510' }}
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl font-bold text-nerd-white mb-3">
              Inizia il tuo <span className="text-gradient">Respawn</span> reale
            </h2>
            <p className="text-nerd-gray text-sm max-w-md mx-auto leading-relaxed">
              Pronto a testare le tue decisioni? Gioca il Libro-Game, scopri la tua profilazione attuale e sblocca gratis la tua Guida di Sopravvivenza!
            </p>
          </div>
          
          <InteractiveLibroGame />
        </div>
      </section>
    </>
  )
}
